import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep, merge } from 'lodash';
import {
  OBJECT_SELECTION_MODE,
  RAW_MAP_KEY_NAME,
  REVERSE_PROPERTY_MAP,
  REVERSE_TILING_MAP,
  SELECTED_OBJECT_COUNT_STR,
  SELECTED_OBJECT_TYPE,
  TEXTURE_MAP_TO_TILING_TYPE,
  TEXTURE_NAME_KEY,
  TEXTURE_NAME_KEY__TEXTURE_MAP,
  TILING_NAME_MAP,
  UNITY_OBJECT_TYPE,
} from '../../../config/constant/unityConstants';
import { selectIsStyleOnlyMode, selectUnityData } from './curatorSlicer';
import { selectIsMaterialCopied, selectIsPropCopiedForAlignment } from './shortcutsModuleSlicer';
import { duplicateArrayRemove } from '../../../helpers/jsHelper';
import { RETAIL_AUTOMATION_ROUTE, RETAIL_AUTOMATION_ROUTE_V2 } from '../../../constants/routePathConstants';

// type TObjectConfig = {
//   expertMode: Boolean;
// }

const initialState = {
  selectedObjectsCountStr: SELECTED_OBJECT_COUNT_STR.NONE,
  unitySelectedObjectInfo: {},
  selectedObjectList: [],
  objectPositionAlongAxis: {}, // position here is in meters -> so keep it always meters
  copiedObjectProperties: null, // store proepties of the object when someone clicked "Copy Material" from the context menu
  objectConfigMap: {}, // map of { objectName: TObjectConfig }
  duplicatePropInProgress: false,
  isObjectSelectionStyleable: false,
  // objectPositionAlongAxis: {
  //   value: 22,
  //   transformType: 'Move',
  // },

  tempUnitySelectedObjectInfo: null, // this is copy of `unitySelectedObjectInfo`. Use it when you need to save object data temporary -> for example when we seelct custom map we apply custom map every time when we click on texture -> but we need to reset object to state before we started to select textures if user clicked on cancel button instead of applying the texture
  showInfoWarningPopup: false,
  infoWarningPopupMessage: null
};

const curatorUnityObjectSlicer = createSlice({
  name: 'curatorUnityObject',
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    storeTempObjectData: (state) => {
      const temp = cloneDeep(state.unitySelectedObjectInfo);
      state.tempUnitySelectedObjectInfo = temp
    },
    clearTempObjectData: (state) => {
      state.tempUnitySelectedObjectInfo = null;
    },
    setUnitySelectedObjectInfo: (state, { payload }) => {
      state.unitySelectedObjectInfo = payload;
    },
    setTextureMap: (state, { payload }) => {
      state.unitySelectedObjectInfo.parsed[payload.tilingTypeName].texture = payload.texture;
    },
    updateRawObject: (state, { payload }) => {
      state.unitySelectedObjectInfo.raw = merge({}, state.unitySelectedObjectInfo.raw, payload);
    },
    updateUnityObject: (state, { payload }) => {
      state.unitySelectedObjectInfo.parsed = merge(
        {},
        state.unitySelectedObjectInfo.parsed,
        payload
      );
    },
    updateParsedObject: (state, { payload }) => {
      state.unitySelectedObjectInfo.parsed = payload
    },
    updateUnityObjectByTilingType: (state, { payload }) => {
      const keyName = TILING_NAME_MAP[payload.tilingType];
      state.unitySelectedObjectInfo.parsed = merge({}, state.unitySelectedObjectInfo.parsed, {
        [keyName]: payload.data,
      });
    },
    updateUnityObjectByTextureName: (state, { payload }) => {
      const { textureName, data } = payload;

      Object.values(TEXTURE_NAME_KEY).forEach(key => {
        if (state.unitySelectedObjectInfo.raw[key] !== textureName) return;

        const mapType = TEXTURE_NAME_KEY__TEXTURE_MAP[key];
        const tilingType  = TEXTURE_MAP_TO_TILING_TYPE[mapType];
        const keyName = TILING_NAME_MAP[tilingType]

        state.unitySelectedObjectInfo.parsed = merge({}, state.unitySelectedObjectInfo.parsed, {
          [keyName]: data,
        });
      })
      
    },
    setIsObjectSelectionStyleable: (state, { payload }) => {
      state.isObjectSelectionStyleable = payload;
    },
    setSelectedObjectsCountStr: (state, { payload }) => {
      state.selectedObjectsCountStr = payload;
    },
    setSelectedObjectList: (state, { payload }) => {
      state.selectedObjectList = payload;
    },
    setObjectPositionAlongAxis: (state, { payload }) => {
      state.objectPositionAlongAxis = {
        value: payload.value,
        transformType: payload.transformType,
      };
    },
    setObjectPositionAlongAxisValue: (state, { payload }) => {
      state.objectPositionAlongAxis.value = payload;
    },
    resetObjectPositionAlongAxis: (state, { payload }) => {
      state.objectPositionAlongAxis = initialState.objectPositionAlongAxis;
    },
    setCopiedObjectProperties: (state, { payload }) => {
      state.copiedObjectProperties = payload;
    },
    setDuplicatePropInProgress: (state, { payload }) => {
      state.duplicatePropInProgress = payload;
    },
    setExpertMode: (state, { payload }) => {
      const { objectName, enabled } = payload
      if (!state.objectConfigMap[objectName]) {
        state.objectConfigMap[objectName] = {}
      }

      state.objectConfigMap[objectName].expertMode = enabled;

      if (!enabled) {
        // recalculate if sliders should be visible when expert mode is off
        Object.values(TILING_NAME_MAP).forEach(key => {
          const show = state.unitySelectedObjectInfo.parsed[key].intensity > 0;
          state.unitySelectedObjectInfo.parsed[key].showSliderWhenExpanded = show;
        })
      }
    },
    setShowInfoWarningPopup: (state, { payload }) => {
      state.showInfoWarningPopup = payload;
    },
    setInfoWarningPopupMessage: (state, { payload }) => {
      state.infoWarningPopupMessage = payload;
    },
    // handleDeletedObjects: (state, { paylaod }) => {
    //   if (!state.unitySelectedObjectInfo.raw) return;
    //   payload.objectList.forEach(object => {
    //     if (object.objectName)
    //   })
    // }
  },
});

export const {
  // setBase64Data,
  setUnitySelectedObjectInfo,
  updateUnityObject,
  updateParsedObject,
  setTextureMap,
  updateRawObject,
  setSelectedObjectsCountStr,
  setSelectedObjectList,
  setObjectPositionAlongAxis,
  resetObjectPositionAlongAxis,
  updateUnityObjectByTextureName,
  setObjectPositionAlongAxisValue,
  setCopiedObjectProperties,
  clearTempObjectData,
  storeTempObjectData,
  updateUnityObjectByTilingType,
  setDuplicatePropInProgress,
  setExpertMode,
  setIsObjectSelectionStyleable,
  setShowInfoWarningPopup,
  setInfoWarningPopupMessage
} = curatorUnityObjectSlicer.actions;
export const curatorUnityObjectActions = curatorUnityObjectSlicer.actions;

export const curatorUnityObjectSelector = (state) => state.curatorUnityObject;
export const curatorUnityObjectReducer = curatorUnityObjectSlicer.reducer;
export default curatorUnityObjectReducer;

export const selectUnitySelectedObjectInfo = (state) =>
  state.curatorUnityObject.unitySelectedObjectInfo;
export const selectUnitySelectedObjectInfoRaw = (state) =>
  state.curatorUnityObject.unitySelectedObjectInfo?.raw;
export const selectUnitySelectedObjectInfoParsed = (state) =>
  state.curatorUnityObject.unitySelectedObjectInfo?.parsed;
export const selectIsAnyObjectSelected = (state) => state.curatorUnityObject.selectedObjectList.length > 0;
  
export const selectSelectedObjectName = (state) =>
  state.curatorUnityObject.unitySelectedObjectInfo?.raw?.objectName;
export const selectSelectedObjectMaterialId = (state) =>
  state.curatorUnityObject.unitySelectedObjectInfo?.raw?.loadedMaterialId;
export const selectHasSelectedObjectInfo = (state) =>
  Boolean(state.curatorUnityObject.unitySelectedObjectInfo?.raw?.objectName);
export const selectIsStyleableObject = (state) => {
  // return Boolean(state.curatorUnityObject.unitySelectedObjectInfo?.raw?.objectName);
  return state.curatorUnityObject.isObjectSelectionStyleable;
  // return true;
}
export const selectHasObjectProperties = (state) => {
  return Boolean(state.curatorUnityObject.unitySelectedObjectInfo?.raw?.objectName);
}
export const selectSelectedObjectCountStr = (state) =>
  Boolean(state.curatorUnityObject.selectedObjectsCountStr);
export const selectIsOneObjectSelected = (state) =>
  selectSelectedObjectList(state).length === 1;

export const selectIsMultiplePropSelected = (state) => {
  const list = state.curatorUnityObject.selectedObjectList;
  const selectedItem = list.filter(a => a.type === UNITY_OBJECT_TYPE.PROP && a.objectSelectionMode === OBJECT_SELECTION_MODE.TRANSFORM);
  const isOnlyGroupProp =  selectedItem.every(a => a.isGroupSelected) && duplicateArrayRemove(selectedItem.map(a => a.groupObjectName)).length === 1;
  return {
    isOnlyGroupProp,
    isMultiPropSelected: selectedItem.length > 1,
    isSinglePropSelected: selectedItem.length > 0
  }
}

export const selectCanReplaceProp = (state) => {
  const list = state.curatorUnityObject.selectedObjectList;
  const { isOnlyGroupProp } = selectIsMultiplePropSelected(state);
  const isRetailAutomation = window.location.pathname.includes(RETAIL_AUTOMATION_ROUTE_V2); // retail-automation
  const selectedItem = list?.[0];
  // return isOnlyGroupProp || (list.length === 1 && 
  //   selectedItem.objectSelectionMode === OBJECT_SELECTION_MODE.TRANSFORM && selectedItem.type === UNITY_OBJECT_TYPE.PROP); // && selectedItem.type === UNITY_OBJECT_TYPE.PROP);
  // return selectedItem.type === UNITY_OBJECT_TYPE.PROP && selectedItem.isParent;
  return isOnlyGroupProp || (list.length && selectedItem.objectSelectionMode === OBJECT_SELECTION_MODE.TRANSFORM) || (list.length && isRetailAutomation); // && selectedItem.type === UNITY_OBJECT_TYPE.PROP);
}

export const selectAllSelectedObjectsStyleable = (state) => {
  const selectedObjectList = selectSelectedObjectList(state);
  const nonStyleableObject = selectedObjectList.find(obj => obj.isStyleAble === false);
  return !nonStyleableObject;
}

export const selectCanPasteMaterial = (state) => {
  if (!selectIsMaterialCopied(state)) return false;

  const list = selectSelectedObjectList(state);
  if (list.length === 0) return false;
  if (list.length === 1) return true;

  return selectIsMaterialCopied(state) && selectAllSelectedObjectsStyleable(state);
}

export const selectCanCopyMaterial = (state) => {
  return selectIsStyleableObject(state);
  // return selectSelectedObjectList(state).length > 0;
} 

export const selectCanDuplicateSelectedObjects = (state) => {
  const canDuplicateAllObjects = state.curatorUnityObject.selectedObjectList
    .every(obj => {
      return obj.objectSelectionMode === OBJECT_SELECTION_MODE.TRANSFORM; // && obj.type !== SELECTED_OBJECT_TYPE.LAYOUT
    });
    
  return canDuplicateAllObjects && !selectIsStyleOnlyMode(state);
}

export const selectIsOnlyLayoutSelected = (state) => {
  return  state.curatorUnityObject.selectedObjectList.every(obj => obj.type === SELECTED_OBJECT_TYPE.LAYOUT && obj.isStatic);
}

export const selectDuplicatePropInProgress = state => state.curatorUnityObject.duplicatePropInProgress;
export const selectSelectedObjectList = (state) => state.curatorUnityObject.selectedObjectList;
export const selectSelectedObjectsCountStr = (state) => state.curatorUnityObject.selectedObjectsCountStr;
export const selectReplaceProp = (state) => selectSelectedObjectList(state) || null;
export const selectIsSingleLightObjectSelected = (state) =>
  Boolean(
    state.curatorUnityObject.selectedObjectList.find((o) => o.type === UNITY_OBJECT_TYPE.LIGHT)
  ) && state.curatorUnityObject.selectedObjectList.length === 1;
export const selectParsedObject = (state) =>
  state.curatorUnityObject.unitySelectedObjectInfo.parsed;
export const selectObjectPositionAlongAxis = (state) =>
  state.curatorUnityObject.objectPositionAlongAxis;
export const selectCopiedObjectProperties = (state) =>
  state.curatorUnityObject.copiedObjectProperties;
export const selectExpertMode = (state, objectName) => {
  objectName = objectName || selectSelectedObjectName(state);
  return Boolean(state.curatorUnityObject.objectConfigMap[objectName]?.expertMode)
}

export const selectTempUnitySelectedObjectInfo = (state) => state.curatorUnityObject.tempUnitySelectedObjectInfo;

export const selectSelectedLightObject = (state) =>
  state.curatorUnityObject.selectedObjectList.find((o) => o.type === UNITY_OBJECT_TYPE.LIGHT);

export const selectSelectedLightObjectList = (state) =>
  state.curatorUnityObject.selectedObjectList?.filter((o) => o.type === UNITY_OBJECT_TYPE.LIGHT);

export const selectMainTilingType = (state) => {
  if (!state.curatorUnityObject.unitySelectedObjectInfo?.parsed) return null;

  const { texture, material } = state.curatorUnityObject.unitySelectedObjectInfo?.parsed;
  return texture.hideUI ? material : texture;
};

export const selectDataByTilingType = (state, tilingOptionType) => {
  const objectInfo = selectUnitySelectedObjectInfo(state);
  const key = TILING_NAME_MAP[tilingOptionType];
  if(tilingOptionType == 1000)
  {
    console.log("1000",objectInfo);
  }
  return objectInfo.parsed?.[key];
};

export const selectRawMapInfoByTilingType = (state, tilingOptionType) => {
  const objectInfo = selectUnitySelectedObjectInfo(state);
  const key = RAW_MAP_KEY_NAME[tilingOptionType];
  return {name: objectInfo.raw?.[key]};
};


export const selectShouldShowColor = (state) => {
  const data = state.curatorUnityObject.unitySelectedObjectInfo?.raw || {};
  const { isTextureChanged, isColor } = data;
  return !isTextureChanged && isColor
}

export const selectColor = (state) => state.curatorUnityObject.unitySelectedObjectInfo?.raw?.colorValue;

export const selectIsTexture = (state) => {
  return Boolean(state.curatorUnityObject.unitySelectedObjectInfo?.parsed?.isTexture)
}

export const selectIsConfiguratorPropSelected = (state) => {
  const unityData = selectUnityData(state);
  if (unityData?.isRetailAutomationEditInCurator) {
    return false;
  }
  const selectedObjectList = selectSelectedObjectList(state);
  return selectedObjectList.some((data) => data.isConfiguratorProp);
}

export const selectShowInfoWarningPopup = (state) => {
  return state.curatorUnityObject.showInfoWarningPopup;
}

export const selectInfoWarningPopupMessage = (state) => {
  return state.curatorUnityObject.infoWarningPopupMessage;
}